import React, { useEffect, useState } from "react";
import ItemBox from "./ItemBox";
import TasksNav from "./TasksNav";
import api from "../../api";
import ItemModal from "./ItemModal";
import { PaginationRow } from "../../components/PaginationRow";
import CenterWrapper from "../../components/CenterWrapper";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { taskStatsAtom } from "../../store/taskStatsAtom";
import GlobalLoadingSpinner from "../../components/GlobalSpinner";
import MOCK_CONVERSATIONS from "../../mock/conversations";
import SectionHeader from "../../components/SectionHeader";
import EmptyState from "../../components/EmptyState";
import NoTasksEmptyStateIcon from "../../icons/NoTasksEmptyStateIcon";

const pageSize = 5;

const Tasks = () => {
  const setTaskStats = useSetRecoilState(taskStatsAtom);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const chatbotUuid = localStorage.getItem("chatbot_uuid");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [singleTask, setSingleTask] = useState({});
  const taskStats = useRecoilValue(taskStatsAtom);

  const totalPages = Math.ceil(totalTasks / pageSize);

  const tasksTypes = [
    {
      name: "Open",
      value: "open",
      count: taskStats.tasks.open,
    },
    {
      name: "Closed",
      value: "closed",
      count: 0,
    },
    {
      name: "All Tickets",
      value: "all",
      count: 0,
    },
  ];

  const [taskType, setTaskType] = useState(tasksTypes[0]);

  const fetchTasks = async () => {
    if (!chatbotUuid) {
      console.error(
        "Chatbot UUID is not found in local storage or URL parameters"
      );
      return;
    }
    setLoading(true);
    setTasks([]);
    let apiFilters;

    switch (taskType.value) {
      case tasksTypes[0].value:
        apiFilters = { resolved: false, handed_off: true };
        break;
      case tasksTypes[1].value:
        apiFilters = { resolved: true, handed_off: true };
        break;
      case tasksTypes[2].value:
        apiFilters = { handed_off: true };
        break;
      default:
        apiFilters = {};
    }

    try {
      const { data } = await api.get(`/api/conversations/${chatbotUuid}`, {
        params: { page, page_size: pageSize, ...apiFilters },
      });
      setTotalTasks(data.total_conversations);
      setTasks(data.conversations);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleConversation = async task => {
    try {
      await api.post("/api/update-conversation", {
        conversation_uuid: task.conversation_uuid,
        resolved: !task.resolved,
      });

      fetchTasks();
    } catch (err) {
      console.error("Failed to update conversation", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskType, page]);

  return (
    <CenterWrapper>
      <div>
        <div className="mb-6">
          <SectionHeader
            title="Tasks"
            description="Use this section to complete all tasks that have been handed off to
            your team."
          />
        </div>

        <TasksNav
          setTaskType={type => {
            setPage(1);
            setTaskType(type);
          }}
          tasksTypes={tasksTypes}
          taskType={taskType}
        />

        <div
          className="flex items-center border rounded-lg px-4 py-2 mt-4 shadow-sm w-64"
          style={{ display: "none" }}
        >
          <input
            type="text"
            placeholder="Search"
            className="flex-1 focus:outline-none text-sm text-gray-700"
          />
          <button className="ml-2 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-4.35-4.35M11 17a6 6 0 100-12 6 6 0 000 12z"
              />
            </svg>
          </button>
        </div>

        {loading && (
          <div className="mt-6 space-y-4 relative">
            <GlobalLoadingSpinner />
            {MOCK_CONVERSATIONS.conversations.map(task => (
              <ItemBox
                task={task}
                key={task.conversation_id}
                setIsModalVisible={false}
                setSingleTask={() => {}}
                handleConversation={() => {}}
              />
            ))}
          </div>
        )}

        {!loading && tasks.length > 0 && (
          <>
            <div className="mt-6 space-y-4 relative">
              {tasks.map(task => (
                <ItemBox
                  task={task}
                  key={task.conversation_id}
                  setIsModalVisible={setIsModalVisible}
                  setSingleTask={setSingleTask}
                  handleConversation={handleConversation}
                />
              ))}
            </div>

            <hr className="my-6 border-t border-gray-300" />

            <PaginationRow
              setPage={setPage}
              totalPages={totalPages}
              currentPage={page}
            />
          </>
        )}

        {!loading && !tasks.length && (
          <div className="flex items-center justify-center mt-24">
            <EmptyState
              title="No tasks found"
              description="Tasks will appear here with a summary of the conversation and follow up action when they’re handed off from your AI agent to your team."
              icon={<NoTasksEmptyStateIcon />}
            />
          </div>
        )}
      </div>

      {singleTask && (
        <ItemModal
          show={isModalVisible}
          setIsModalVisible={setIsModalVisible}
          singleTask={singleTask}
          setSingleTask={setSingleTask}
          handleConversation={handleConversation}
        />
      )}
    </CenterWrapper>
  );
};

export default Tasks;
